import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Lock, FileText } from "lucide-react";
import { http, edition, gradeLabel } from "@/lib/api";
import { StateTabs } from "@/pages/admin/Library";

const STATUS = { missing: ["Not started", "bg-stone-100 text-stone-500"], draft: ["Draft", "bg-[#F3DCC9] text-[#8a4b2a]"], approved: ["Approved", "bg-[#6B9080] text-white"] };

export const CourseList = () => {
  const [st, setSt] = useState(edition.state);
  const [courses, setCourses] = useState(null);
  const [subject, setSubject] = useState("all");
  useEffect(() => { setCourses(null); http.get("/admin/courses", { params: { state: st } }).then((r) => setCourses(r.data)); }, [st]);
  const subjects = courses ? [...new Map(courses.map((c) => [c.subject_id, c.subject_name])).entries()] : [];
  const shown = (courses || []).filter((c) => subject === "all" || c.subject_id === subject);

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-courses-page">
      <p className="text-xs uppercase tracking-[0.25em] text-[#C87D53] font-semibold">Owner console</p>
      <h1 className="font-display text-4xl sm:text-5xl text-[#2C4A3E] mt-2">Course Documents</h1>
      <p className="text-stone-500 mt-2">Syllabus, 36-week pacing guide, unit plans, rubrics, placement test, unit tests and midterm/final (grades 6-12), all with answer keys.</p>
      <div className="flex flex-wrap gap-3 mt-6 items-center">
        <StateTabs value={st} onChange={setSt} testId="courses-state-tabs" />
        <div className="flex flex-wrap gap-1.5">
          {[["all", "All subjects"], ...subjects].map(([k, v]) => (
            <button key={k} onClick={() => setSubject(k)} data-testid={`courses-filter-${k}`}
              className={`px-3 py-1 rounded-full text-xs ${subject === k ? "bg-[#C87D53] text-white" : "bg-white border border-[#E6DEC3] text-stone-600"}`}>{v}</button>
          ))}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
        {shown.map((c) => (
          <Link key={c.id} to={`/admin/courses/${st}/${c.id}`} data-testid={`course-card-${c.id}`}
            className="rounded-2xl p-4 bg-[#F7F3EB] border border-[#E6DEC3] hover:border-[#2C4A3E] transition-colors">
            <p className="text-[10px] uppercase tracking-widest text-stone-500">{c.subject_name} · {gradeLabel(c.grade)}{c.credit ? " · 1.0 credit" : ""}</p>
            <p className="font-display text-[#2C4A3E] mt-1">{c.title}</p>
            <p className="text-xs text-stone-500 mt-2">{c.approved}/{c.doc_total} approved{c.draft ? ` · ${c.draft} draft` : ""}</p>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const CourseDocs = () => {
  const { state, courseId } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { http.get(`/admin/courses/${state}/${courseId}`).then((r) => setD(r.data)); }, [state, courseId]);
  if (!d) return <p className="text-center py-20 text-stone-500">Loading…</p>;
  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-course-docs-page">
      <Link to="/admin/courses" className="inline-flex items-center gap-2 text-sm text-stone-500" data-testid="course-docs-back-link"><ArrowLeft className="w-4 h-4" /> All courses</Link>
      <h1 className="font-display text-4xl text-[#2C4A3E] mt-5" data-testid="course-docs-title">{d.course.title}</h1>
      <p className="text-stone-500 mt-1">{d.course.subject_name} · {gradeLabel(d.course.grade)} · {d.course.standard}</p>
      <div className="mt-8 space-y-3">
        {d.docs.map((x) => (
          <Link key={x.key} to={`/admin/library/${encodeURIComponent(x.key)}`} data-testid={`course-doc-${x.doc}`}
            className="flex items-center gap-4 rounded-2xl bg-white/60 border border-[#E6DEC3] p-4 hover:border-[#2C4A3E] transition-colors">
            {x.visibility === "private" ? <Lock className="w-4 h-4 text-[#C87D53]" /> : <FileText className="w-4 h-4 text-[#C87D53]" />}
            <div className="flex-1"><p className="text-[#2C4A3E] font-medium">{x.title.split(": ").pop()}</p><p className="text-xs text-stone-500">{x.summary}</p></div>
            <span className={`px-2.5 py-0.5 rounded-full text-xs ${STATUS[x.status][1]}`} data-testid={`course-doc-status-${x.doc}`}>{STATUS[x.status][0]}{x.version ? ` v${x.version}` : ""}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};
